import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Col, Form, Input, Layout, Row, Select } from 'antd';
import generator from 'generate-password-browser';

import CabinetHeader from '../../../../components/Header';

import clientActions from '../../../client/actions';
import CLIENT from '../../../client/actionTypes';
import { ClientPersonalInfoType } from '../../../client/types';
import DialogRecoveryPassword from '../RecoveryPassword/DialogRecoveryPassword';

import { AppDispatch, ReduxState } from '../../../../types/app';

const { Content } = Layout;

type FormValues = Partial<ClientPersonalInfoType>;

type PasswordFormValues = {
  Type: 'auth';
  PasswordCurrent: string;
  PasswordNew: string;
  PasswordNewConfirm: string;
};

type ProfileFieldProps = {
  id: string;
  labelId: string;
  name: string;
  rules?: Array<Record<string, unknown>>;
  helpId?: string;
  className?: string;
  children: React.ReactNode;
};

const ProfileField = ({
  id,
  labelId,
  name,
  rules,
  helpId,
  className,
  children,
}: ProfileFieldProps) => {
  const intl = useIntl();

  return (
    <label
      className={`profile-form__field${helpId ? ' profile-form__field_with-hint' : ''}${className ? ` ${className}` : ''}`}
      htmlFor={id}
    >
      <span className="profile-form__label">
        {intl.formatMessage({ id: labelId })}
      </span>

      <Form.Item name={name} noStyle rules={rules}>
        {children}
      </Form.Item>

      {helpId && (
        <span className="profile-form__hint">
          {intl.formatMessage({ id: helpId })}
        </span>
      )}
    </label>
  );
};

const DEFAULT_PHONE_REGEXP = '^[0-9+\\- ]+$';
const EMPTY_LANGS: string[] = [];

const ProfilePage = () => {
  const intl = useIntl();

  const dispatch = useDispatch<AppDispatch>();

  const [form] = Form.useForm<FormValues>();

  const [passwordForm] = Form.useForm<PasswordFormValues>();

  const [isChanged, setIsChanged] = useState(false);

  const initialValues = useSelector(
    (state: ReduxState) => state.ClientPersonalInfo.info
  );
  const phoneRegExp =
    useSelector(
      (state: ReduxState) => state.Data?.config?.client?.phoneRegExp
    ) || DEFAULT_PHONE_REGEXP;
  const langs =
    useSelector((state: ReduxState) => state.Data?.config?.langs) ||
    EMPTY_LANGS;
  const email = useSelector(
    (state: ReduxState) => state.ClientPersonalInfo.info?.Email ?? ''
  );
  const saveReadyStatus = useSelector(
    (state: ReduxState) => state.ClientPersonalInfo.saveReadyStatus
  );
  const changePasswordReadyStatus = useSelector(
    (state: ReduxState) => state.ClientAuth.changePasswordReadyStatus
  );
  const otherBroker = useSelector(
    (state: ReduxState) => state.Client.data?.OtherBroker
  );

  useEffect(() => {
    void dispatch(clientActions.PersonalInfo.loadIfNeeded());
  }, [dispatch]);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);

      setIsChanged(false);
    }
  }, [form, initialValues]);

  useEffect(() => {
    if (changePasswordReadyStatus === CLIENT.AUTH.CHANGE_PASSWORD.SUCCESS) {
      passwordForm.resetFields();
    }
  }, [changePasswordReadyStatus, passwordForm]);

  const onSave = (values: FormValues) => {
    const {
      Phone,

      PasswordPhone,

      Country,

      State,

      City,

      ZipCode,

      Address,

      Lang,
    } = values;

    dispatch(
      clientActions.PersonalInfo.save({
        Phone,

        PasswordPhone,

        Country,

        State,

        City,

        ZipCode,

        Address,

        Lang,
      })
    );
  };

  const onChangePassword = (values: PasswordFormValues) => {
    dispatch(
      clientActions.Auth.changePassword({
        ...values,
        Type: 'auth',
      })
    );
  };

  const generatePassword = () => {
    const password = generator.generate({
      length: 12,

      numbers: true,
    });

    passwordForm.setFieldsValue({
      PasswordNew: password,

      PasswordNewConfirm: password,
    });
  };

  const onValuesChange = (_: Partial<FormValues>, formValues: FormValues) => {
    if (!initialValues) return;

    const changed =
      Object.keys(formValues).filter(
        (key: keyof FormValues) => initialValues[key] !== formValues[key]
      ).length > 0;

    if (changed !== isChanged) {
      setIsChanged(changed);
    }
  };

  const langOptions = langs.map((lang: string) => ({
    value: lang,

    label: intl.formatMessage({
      id: `lang.${lang}`,

      defaultMessage: lang,
    }),
  }));

  return (
    <Layout className="profile-page">
      <CabinetHeader />

      <Content className="profile-page__content">
        <h1 className="profile-page__title">
          {intl.formatMessage({ id: 'profile.title' })}
        </h1>

        <Form
          form={form}
          className="profile-form"
          layout="vertical"
          requiredMark={false}
          onFinish={onSave}
          onValuesChange={onValuesChange}
          scrollToFirstError
        >
          <section
            className="profile-form__section"
            aria-labelledby="profile-personal-info"
          >
            <div className="profile-form__section-head">
              <h2
                className="profile-form__section-title"
                id="profile-personal-info"
              >
                {intl.formatMessage({ id: 'profile.personalInfo' })}
              </h2>
            </div>

            <div className="profile-form__fields">
              <label className="profile-form__field" htmlFor="profile-email">
                <span className="profile-form__label">
                  {intl.formatMessage({ id: 'profile.field.Email' })}
                </span>

                <Input
                  id="profile-email"
                  className="profile-form__input"
                  variant="borderless"
                  readOnly
                  value={email}
                />
              </label>

              <Row gutter={20}>
                <Col xs={24} sm={12}>
                  <ProfileField
                    id="profile-phone"
                    labelId="profile.field.Phone"
                    name="Phone"
                    rules={[{ pattern: new RegExp(phoneRegExp) }]}
                  >
                    <Input
                      id="profile-phone"
                      className="profile-form__input"
                      variant="borderless"
                    />
                  </ProfileField>
                </Col>

                <Col xs={24} sm={12}>
                  <ProfileField
                    id="profile-password-phone"
                    labelId="profile.field.PasswordPhone"
                    name="PasswordPhone"
                    rules={[{ max: 100 }]}
                  >
                    <Input
                      id="profile-password-phone"
                      className="profile-form__input"
                      variant="borderless"
                    />
                  </ProfileField>
                </Col>
              </Row>

              <Row gutter={20}>
                <Col xs={24} sm={12}>
                  <ProfileField
                    id="profile-country"
                    labelId="profile.field.Country"
                    name="Country"
                    rules={[{ max: 100 }]}
                  >
                    <Input
                      id="profile-country"
                      className="profile-form__input"
                      variant="borderless"
                    />
                  </ProfileField>
                </Col>

                <Col xs={24} sm={12}>
                  <ProfileField
                    id="profile-state"
                    labelId="profile.field.State"
                    name="State"
                    rules={[{ max: 100 }]}
                  >
                    <Input
                      id="profile-state"
                      className="profile-form__input"
                      variant="borderless"
                    />
                  </ProfileField>
                </Col>
              </Row>

              <Row gutter={20}>
                <Col xs={24} sm={12}>
                  <ProfileField
                    id="profile-city"
                    labelId="profile.field.City"
                    name="City"
                    rules={[{ max: 100 }]}
                  >
                    <Input
                      id="profile-city"
                      className="profile-form__input"
                      variant="borderless"
                    />
                  </ProfileField>
                </Col>

                <Col xs={24} sm={12}>
                  <ProfileField
                    id="profile-zip-code"
                    labelId="profile.field.ZipCode"
                    name="ZipCode"
                    rules={[{ max: 100 }]}
                  >
                    <Input
                      id="profile-zip-code"
                      className="profile-form__input"
                      variant="borderless"
                    />
                  </ProfileField>
                </Col>
              </Row>

              <Row gutter={20}>
                <Col xs={24} sm={12}>
                  <ProfileField
                    id="profile-address"
                    labelId="profile.field.Address"
                    name="Address"
                    rules={[{ max: 1000 }]}
                  >
                    <Input
                      id="profile-address"
                      className="profile-form__input"
                      variant="borderless"
                    />
                  </ProfileField>
                </Col>

                <Col xs={24} sm={12}>
                  <ProfileField
                    id="profile-lang"
                    labelId="profile.field.Lang"
                    name="Lang"
                    className="profile-form__field_lang"
                    rules={[{ required: true }]}
                  >
                    <Select
                      id="profile-lang"
                      className="profile-form__select"
                      classNames={{
                        popup: {
                          root: 'select-popup',
                        },
                      }}
                      variant="borderless"
                      options={langOptions}
                    />
                  </ProfileField>
                </Col>
              </Row>
            </div>

            <Button
              type="primary"
              className="profile-form__save"
              htmlType="submit"
              disabled={!isChanged}
              loading={saveReadyStatus === CLIENT.PERSONAL_INFO.SAVE.REQUESTING}
              style={{ marginTop: 20 }}
            >
              {intl.formatMessage({ id: 'profile.save' })}
            </Button>
          </section>
        </Form>

        {!otherBroker && (
          <Form
            form={passwordForm}
            className="profile-form form-password"
            layout="vertical"
            requiredMark={false}
            onFinish={onChangePassword}
            initialValues={{ Type: 'auth' }}
            scrollToFirstError
          >
            <Form.Item name="Type" hidden>
              <Input type="hidden" />
            </Form.Item>

            <section
              className="profile-form__section"
              aria-labelledby="profile-password"
            >
              <h2 className="profile-form__section-title" id="profile-password">
                {intl.formatMessage({ id: 'profile.password' })}
              </h2>

              <div className="profile-form__fields">
                <div className="profile-form__field profile-form__field_password">
                  <Form.Item
                    name="PasswordCurrent"
                    rules={[{ required: true }, { type: 'string', min: 5 }]}
                  >
                    <Input.Password
                      id="profile-old-password"
                      className="profile-form__input profile-form__input_placeholder"
                      variant="borderless"
                      placeholder={intl.formatMessage({
                        id: 'profile.password.old',
                      })}
                      visibilityToggle={false}
                    />
                  </Form.Item>

                  <div className="profile-form__forgot-wrap">
                    <DialogRecoveryPassword />
                  </div>
                </div>

                <Row gutter={[16, 8]} align="middle">
                  <Col xs={24} sm={16}>
                    <div className="profile-form__field">
                      <Form.Item
                        name="PasswordNew"
                        rules={[{ required: true }, { type: 'string', min: 5 }]}
                      >
                        <Input.Password
                          id="profile-new-password"
                          className="profile-form__input profile-form__input_placeholder"
                          variant="borderless"
                          placeholder={intl.formatMessage({
                            id: 'profile.password.new',
                          })}
                        />
                      </Form.Item>
                    </div>
                  </Col>

                  <Col xs={24} sm={8}>
                    <Button
                      onClick={generatePassword}
                      className="profile-form__generate-btn"
                      tabIndex={-1}
                    >
                      {intl.formatMessage({ id: 'buttons.generate' })}
                    </Button>
                  </Col>
                </Row>

                <label
                  className="profile-form__field"
                  htmlFor="profile-repeat-password"
                >
                  <Form.Item
                    name="PasswordNewConfirm"
                    dependencies={['PasswordNew']}
                    rules={[
                      { required: true },

                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            !value ||
                            getFieldValue('PasswordNew') === value
                          ) {
                            return Promise.resolve();
                          }

                          return Promise.reject(
                            intl.formatMessage({ id: 'error.MatchField' })
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      id="profile-repeat-password"
                      className="profile-form__input profile-form__input_placeholder"
                      variant="borderless"
                      placeholder={intl.formatMessage({
                        id: 'profile.password.repeat',
                      })}
                      visibilityToggle={false}
                    />
                  </Form.Item>
                </label>
              </div>

              <Button
                type="primary"
                className="profile-form__save"
                htmlType="submit"
                loading={
                  changePasswordReadyStatus ===
                  CLIENT.AUTH.CHANGE_PASSWORD.REQUESTING
                }
                style={{ marginTop: 20 }}
              >
                {intl.formatMessage({ id: 'buttons.changePassword' })}
              </Button>
            </section>
          </Form>
        )}
      </Content>
    </Layout>
  );
};

export default ProfilePage;
